/**
 * CreateFolderModal — modal for creating a new folder.
 */

import { useState } from 'react'
import { X } from 'lucide-react'

interface CreateFolderModalProps {
  parentName: string
  onClose: () => void
  onCreate: (name: string, folderType: string, createDefaultChildren: boolean) => Promise<void>
}

export default function CreateFolderModal({ parentName, onClose, onCreate }: CreateFolderModalProps) {
  const [name, setName] = useState('')
  const [folderType, setFolderType] = useState<'custom' | 'date_bucket'>('custom')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setLoading(true)
    try {
      await onCreate(name.trim(), folderType, folderType === 'date_bucket')
      onClose()
    } catch {
      // Error handled by caller
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rq-modal-overlay" onClick={onClose}>
      <div className="rq-modal-card" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[var(--rq-text)]">New Folder</h3>
          <button onClick={onClose} className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)]">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-[var(--rq-text-soft)] mb-4">
          Creating inside <span className="font-medium text-[var(--rq-text)]">{parentName}</span>
        </p>

        <form onSubmit={handleSubmit}>
          <label className="block text-xs text-[var(--rq-text-soft)] mb-1">Folder name</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. B-Roll, Interviews, Day 2..."
            className="w-full rq-input-dark text-sm mb-3"
          />

          <label className="block text-xs text-[var(--rq-text-soft)] mb-1">Folder type</label>
          <div className="flex gap-2 mb-5">
            <button
              type="button"
              onClick={() => setFolderType('custom')}
              className={`flex-1 text-sm py-2 px-3 rounded-lg border transition-colors ${
                folderType === 'custom'
                  ? 'border-[var(--rq-blue)] bg-[var(--rq-blue)]/10 text-[var(--rq-text)]'
                  : 'border-white/10 text-[var(--rq-text-soft)] hover:border-white/20'
              }`}
            >
              Custom
            </button>
            <button
              type="button"
              onClick={() => setFolderType('date_bucket')}
              className={`flex-1 text-sm py-2 px-3 rounded-lg border transition-colors ${
                folderType === 'date_bucket'
                  ? 'border-[var(--rq-amber)] bg-[var(--rq-amber)]/10 text-[var(--rq-text)]'
                  : 'border-white/10 text-[var(--rq-text-soft)] hover:border-white/20'
              }`}
            >
              Date Bucket
            </button>
          </div>

          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name.trim() || loading}
              className="rq-btn-primary px-4 py-2 text-sm disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Folder'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
